import { CloseIcon } from "@kupikupi/ui";
import { type ChangeEvent, type CSSProperties, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  /** Picked a local image File (camera / gallery / drag-drop). */
  onPicked: (file: File) => void;
  /** Confirmed the previewed image → continue to the crop step. */
  onConfirm: () => void;
  /** Preview data-URL of the prepared (downscaled) image, or "" while none. */
  previewSrc: string;
  /** True while the picked file is being decoded / downscaled. */
  preparing: boolean;
  onCancel: () => void;
}

/**
 * Hidden file inputs use 1px + opacity:0 (NOT `display:none`): some Android
 * WebViews — including Telegram's — fire a programmatic `.click()` unreliably
 * on a `display:none` input. See issue #925.
 */
const HIDDEN_FILE_INPUT: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: "none",
};

/**
 * Mini-App image-search upload sheet — a TG bottom-sheet port of
 * apps/web/components/image-search/upload-modal.tsx.
 *
 * Telegram differences vs Web:
 *  - Bottom sheet (slides up) instead of a centered card — native TG feel.
 *  - No URL paste: the Mini has no /api/proxy-image route, so a URL field would
 *    be a dead control. Camera / gallery / drag-drop only.
 *  - Preview + reselect + "Next" → crop: lets the user confirm the right photo
 *    before the (slower) region-detect step, and shows a loading spinner while
 *    the file is decoded/downscaled (requirement: upload must show loading).
 *
 * Android camera fix (#925): Telegram's Android WebChromeClient ignores a plain
 * `<input type=file>` for the camera and only offers the gallery/files picker.
 * We expose TWO inputs — a dedicated "take photo" one with
 * `capture="environment"` (newer Telegram Android honors `isCaptureEnabled()`
 * → opens the camera) and a plain "choose from gallery" one. iOS is unaffected:
 * its WKWebView sheet already offers both; the explicit buttons just map 1:1.
 * Worst case (old Telegram / device that still ignores capture): the camera
 * button degrades to the gallery picker — never an error, and the gallery
 * button is always present as a fallback.
 */
export function UploadModal({ onPicked, onConfirm, previewSrc, preparing, onCancel }: Props) {
  const { t } = useTranslation("common");
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const galleryRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const pickFirstImage = (files: FileList | null) => {
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (f?.type.startsWith("image/")) {
        setError(null);
        onPicked(f);
        return;
      }
    }
    setError(t("image_search.err_not_image"));
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    pickFirstImage(e.target.files);
    // allow re-picking / re-shooting the same file
    e.target.value = "";
  };

  const hasPreview = !!previewSrc;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss; close button is keyboard-accessible
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="kk-mini-upload-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(11, 12, 15, 0.55)",
        zIndex: 1100,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !preparing) onCancel();
      }}
    >
      <div
        style={{
          background: "var(--color-surface, #fff)",
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          padding: "18px 16px calc(18px + env(safe-area-inset-bottom, 0px))",
          width: "100%",
          maxHeight: "88vh",
          overflowY: "auto",
          boxShadow: "0 -8px 30px rgba(0,0,0,0.22)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <h2 id="kk-mini-upload-title" style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>
            {t("image_search.upload_title")}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label={t("image_search.close")}
            style={{
              background: "transparent",
              border: 0,
              cursor: "pointer",
              color: "var(--color-text-muted)",
              padding: 4,
              display: "inline-flex",
            }}
          >
            <CloseIcon size={20} />
          </button>
        </div>

        {hasPreview ? (
          // ── Preview + reselect ──
          <div style={{ display: "grid", placeItems: "center", gap: 12 }}>
            <div
              style={{
                position: "relative",
                width: "100%",
                maxWidth: 320,
                borderRadius: 12,
                overflow: "hidden",
                background: "var(--color-surface-muted, #f2f2f2)",
                display: "grid",
                placeItems: "center",
              }}
            >
              {/* biome-ignore lint/performance/noImgElement: data-URL preview, not a CDN asset */}
              <img
                src={previewSrc}
                alt={t("image_search.upload_title")}
                style={{ width: "100%", maxHeight: "46vh", objectFit: "contain", display: "block" }}
              />
            </div>
            <button
              type="button"
              onClick={() => galleryRef.current?.click()}
              style={{
                background: "transparent",
                border: 0,
                color: "var(--color-primary-fill, #123329)",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                padding: "4px 8px",
              }}
            >
              {t("image_search.preview_reselect")}
            </button>
          </div>
        ) : (
          // ── Dropzone (drag-drop on desktop; tap = gallery) ──
          // biome-ignore lint/a11y/noStaticElementInteractions: dropzone
          // biome-ignore lint/a11y/useKeyWithClickEvents: dropzone (camera/gallery buttons below are keyboard-accessible)
          <div
            onClick={() => galleryRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              pickFirstImage(e.dataTransfer?.files ?? null);
            }}
            style={{
              border: `2px dashed ${dragOver ? "var(--color-primary, #0BA888)" : "var(--color-border, #ddd)"}`,
              borderRadius: 14,
              padding: "28px 16px",
              textAlign: "center",
              cursor: "pointer",
              background: dragOver
                ? "color-mix(in srgb, var(--color-primary, #0BA888) 6%, transparent)"
                : "transparent",
            }}
          >
            <div style={{ fontSize: 32, lineHeight: 1, marginBottom: 8 }} aria-hidden>
              📷
            </div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{t("image_search.upload_hint")}</div>
            <div style={{ fontSize: 12, color: "var(--color-text-muted, #888)", marginTop: 6 }}>
              {t("image_search.upload_formats")}
            </div>
          </div>
        )}

        {/* Camera input: capture="environment" → rear camera. No `multiple`
            (Android pickers misbehave with multiple on a capture input). */}
        <input
          ref={cameraRef}
          data-testid="kk-mini-upload-camera"
          type="file"
          accept="image/*"
          capture="environment"
          style={HIDDEN_FILE_INPUT}
          onChange={handleInputChange}
        />
        {/* Gallery input: no capture → file/gallery picker. */}
        <input
          ref={galleryRef}
          data-testid="kk-mini-upload-file"
          type="file"
          accept="image/*"
          style={HIDDEN_FILE_INPUT}
          onChange={handleInputChange}
        />

        {error && (
          <div style={{ marginTop: 12, color: "var(--color-danger, #d33)", fontSize: 13 }}>
            {error}
          </div>
        )}

        {hasPreview ? (
          // Continue to crop.
          <button
            type="button"
            data-testid="kk-mini-upload-next"
            onClick={() => {
              if (preparing) return;
              onConfirm();
            }}
            disabled={preparing}
            aria-busy={preparing}
            style={ctaStyle(preparing)}
          >
            {preparing && <span className="kk-mini-spinner kk-mini-spinner--sm" aria-hidden />}
            {preparing ? t("image_search.crop_detecting") : t("image_search.next")}
          </button>
        ) : (
          // Pick a source: take photo (camera) or choose from gallery.
          <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
            <button
              type="button"
              data-testid="kk-mini-upload-take-photo"
              onClick={() => {
                if (preparing) return;
                cameraRef.current?.click();
              }}
              disabled={preparing}
              aria-busy={preparing}
              style={ctaStyle(preparing)}
            >
              {preparing && <span className="kk-mini-spinner kk-mini-spinner--sm" aria-hidden />}
              {preparing ? t("image_search.crop_detecting") : t("image_search.take_photo")}
            </button>
            <button
              type="button"
              data-testid="kk-mini-upload-choose-gallery"
              onClick={() => {
                if (preparing) return;
                galleryRef.current?.click();
              }}
              disabled={preparing}
              style={{
                width: "100%",
                padding: "12px 16px",
                background: "transparent",
                color: "var(--color-primary-fill, #123329)",
                border: "1.5px solid var(--color-border, #ddd)",
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 700,
                cursor: preparing ? "not-allowed" : "pointer",
                opacity: preparing ? 0.6 : 1,
              }}
            >
              {t("image_search.choose_gallery")}
            </button>
            {/* Old-Telegram fallback: if the camera button can't open the
                camera, shoot with the system camera then upload from gallery. */}
            <p
              style={{
                margin: "2px 4px 0",
                fontSize: 12,
                lineHeight: 1.45,
                color: "var(--color-text-muted, #888)",
                textAlign: "center",
              }}
            >
              {t("image_search.camera_hint")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/** Primary CTA (orange) style; muted + spinner while preparing. */
function ctaStyle(preparing: boolean): CSSProperties {
  return {
    width: "100%",
    padding: "13px 16px",
    background: preparing ? "var(--color-text-muted, #9ca3af)" : "var(--color-cta, #FF6B4A)",
    color: "#fff",
    border: 0,
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 800,
    cursor: preparing ? "not-allowed" : "pointer",
    opacity: preparing ? 0.7 : 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  };
}
